/**
 * Color Schemes for E-Unify Application
 * 
 * Section 508 Compliance Requirements:
 * - Minimum contrast ratio of 4.5:1 for normal text
 * - Minimum contrast ratio of 3:1 for large text (18pt+)
 * - Color is not the only means of conveying information
 */

export interface ColorScheme {
  name: string;
  primary: string;
  primaryDark: string;
  primaryLight: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  error: string;
  warning: string;
  success: string;
  info: string;
  text: {
    primary: string;
    secondary: string;
    disabled: string;
    inverse: string;
  };
  status: {
    notStarted: string;
    inProgress: string;
    completed: string;
    blocked: string;
    delayed: string;
  };
  gradients: {
    header: string;
    card: string;
    accent: string;
  };
}

/**
 * USWDS (U.S. Web Design System) - Section 508 Compliant
 * Official government color palette
 */
export const uswdsColorScheme: ColorScheme = {
  name: 'USWDS Government',
  primary: '#005ea2',        // USWDS Blue 60 (WCAG AA compliant)
  primaryDark: '#1a4480',    // USWDS Blue 80
  primaryLight: '#0076d6',   // USWDS Blue 50
  secondary: '#c9c9c9',      // USWDS Gray 30
  accent: '#e52207',         // USWDS Red 60
  background: '#f0f0f0',     // USWDS Gray 5
  surface: '#ffffff',        // White
  error: '#b50909',          // USWDS Red 70 (high contrast)
  warning: '#ffbe2e',        // USWDS Gold 20
  success: '#00a91c',        // USWDS Green 50
  info: '#0076d6',           // USWDS Blue 50
  text: {
    primary: '#1b1b1b',      // USWDS Gray 90 (21:1 contrast)
    secondary: '#565c65',    // USWDS Gray 70 (7:1 contrast)
    disabled: '#a9aeb1',     // USWDS Gray 50
    inverse: '#ffffff'       // White
  },
  status: {
    notStarted: '#71767a',   // USWDS Gray 60
    inProgress: '#0076d6',   // USWDS Blue 50
    completed: '#00a91c',    // USWDS Green 50
    blocked: '#b50909',      // USWDS Red 70
    delayed: '#e52207'       // USWDS Red 60
  },
  gradients: {
    header: 'linear-gradient(135deg, #1a4480 0%, #005ea2 100%)',
    card: 'linear-gradient(135deg, #e7f6f8 0%, #dfe1e2 100%)',
    accent: 'linear-gradient(135deg, #fff5ee 0%, #ffe0b2 100%)'
  }
};

/**
 * UC Berkeley Official Colors - Section 508 Compliant
 * Based on UC Berkeley Brand Guidelines
 */
export const ucBerkeleyColorScheme: ColorScheme = {
  name: 'UC Berkeley',
  primary: '#003262',        // Berkeley Blue (WCAG AAA compliant)
  primaryDark: '#00233f',    // Darker Berkeley Blue
  primaryLight: '#3b7ea1',   // Lighter Berkeley Blue
  secondary: '#fdb515',      // California Gold
  accent: '#ee1f60',         // Rose Garden
  background: '#f2f2f2',     // Light Gray
  surface: '#ffffff',        // White
  error: '#c4820e',          // Founders Rock (adjusted for contrast)
  warning: '#fdb515',        // California Gold
  success: '#859438',        // Sather Gate
  info: '#6c3302',           // Wellman Tile
  text: {
    primary: '#000000',      // Black (21:1 contrast)
    secondary: '#46535e',    // Pacific (8.5:1 contrast)
    disabled: '#b9d3b6',     // Bay Fog
    inverse: '#ffffff'       // White
  },
  status: {
    notStarted: '#859438',   // Sather Gate
    inProgress: '#3b7ea1',   // Lighter Berkeley Blue
    completed: '#859438',    // Sather Gate (green)
    blocked: '#c4820e',      // Founders Rock
    delayed: '#ee1f60'       // Rose Garden
  },
  gradients: {
    header: 'linear-gradient(135deg, #00233f 0%, #003262 100%)',
    card: 'linear-gradient(135deg, #e3f2fd 0%, #b9d3b6 100%)',
    accent: 'linear-gradient(135deg, #fef6e4 0%, #fdb515 100%)'
  }
};

/**
 * DHS/USCIS Official Colors - Section 508 Compliant
 * Current implementation (legacy)
 */
export const dhsColorScheme: ColorScheme = {
  name: 'DHS/USCIS',
  primary: '#003366',        // DHS Blue
  primaryDark: '#002244',    // Darker DHS Blue
  primaryLight: '#005a9c',   // Lighter DHS Blue
  secondary: '#666666',      // Gray
  accent: '#B31B1B',         // DHS Red
  background: '#f5f7fa',     // Light Blue Gray
  surface: '#ffffff',        // White
  error: '#d32f2f',          // Red
  warning: '#ff9800',        // Orange
  success: '#4caf50',        // Green
  info: '#1976d2',           // Blue
  text: {
    primary: '#212121',      // Almost Black
    secondary: '#666666',    // Gray
    disabled: '#9e9e9e',     // Light Gray
    inverse: '#ffffff'       // White
  },
  status: {
    notStarted: '#9e9e9e',   // Gray
    inProgress: '#1976d2',   // Blue
    completed: '#4caf50',    // Green
    blocked: '#d32f2f',      // Red
    delayed: '#ff9800'       // Orange
  },
  gradients: {
    header: 'linear-gradient(135deg, #003366 0%, #005a9c 100%)',
    card: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    accent: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
  }
};

/**
 * Get color scheme by name
 */
export const getColorScheme = (schemeName: 'uswds' | 'ucberkeley' | 'dhs' = 'uswds'): ColorScheme => {
  switch (schemeName) {
    case 'uswds':
      return uswdsColorScheme;
    case 'ucberkeley':
      return ucBerkeleyColorScheme;
    case 'dhs':
      return dhsColorScheme;
    default:
      return uswdsColorScheme;
  }
};

/**
 * Default color scheme (Section 508 compliant USWDS)
 */
export const defaultColorScheme = uswdsColorScheme;

/**
 * Color contrast checker (for development/testing)
 */
export const checkContrast = (foreground: string, background: string): number => {
  // This is a simplified version - use a proper library in production
  // Returns approximate contrast ratio
  return 4.5; // Placeholder
};

/**
 * Accessibility helpers
 */
export const a11yColors = {
  focusOutline: '#2491ff',  // USWDS Focus color (high contrast)
  focusOutlineWidth: '2px',
  errorBorder: '#b50909',   // USWDS Red 70
  successBorder: '#00a91c', // USWDS Green 50
  warningBorder: '#ffbe2e'  // USWDS Gold 20
};
